// src/services/characterService.ts
// Character CRUD operations via Figryne Backend API

import { config } from '../config';
import { auth } from './firebase';
import { Character, CharacterImage, CharacterLink, FanartRequest } from '../types/character';
import { generateSecureXorSharesWithProgress, SecureXorShares } from '../utils/xorShareGenerator';

// API Response types
interface CharacterResponse {
  id: string;
  creatorId?: string;  // 作者ID（作成時に設定）
  ownerId: string;
  name: string;
  nameReading?: string;  // 読み仮名
  nameEnglish?: string;  // 英語表記
  category?: 'original' | 'vtuber';  // カテゴリー
  bio: string;
  // XOR-Pix image fields
  noise1Url?: string;
  noise2Url?: string;
  rid?: string;
  imageWidth?: number;
  imageHeight?: number;
  thumbnailUrl?: string;
  // XOR-Pix fallback URLs (signed GCS URLs for CDN failure)
  fallbackNoise1Url?: string;
  fallbackNoise2Url?: string;
  // Legacy field (for backward compatibility)
  avatarUrl?: string;
  headerImageUrl?: string;
  tags: string[];
  cardTheme?: 'dark' | 'light';
  themeColors?: string[];
  isPublic: boolean;
  allowFanart: boolean;
  fanartModerationEnabled?: boolean;
  status: 'draft' | 'active' | 'suspended';
  stats: {
    viewCount: number;
    likeCount: number;
    fanartCount: number;
    followerCount: number;
  };
  createdAt: string;
  updatedAt: string;
  // Creator info (from public API)
  creatorName?: string;
  creatorUsername?: string;
  creatorAvatar?: string;
  // Owner info (from public API)
  ownerName?: string;
  ownerUsername?: string;
  ownerAvatar?: string;
  // AR連携（appeear.me）
  arModelId?: string;
  arModelStatus?: 'processing' | 'active' | 'expired';
  // おむかえ（販売）関連
  forSale?: boolean;
  salePrice?: number;
  transferCount?: number;
  // キャラクター設定
  species?: string;
  gender?: string;
  age?: string;
  birthday?: string;
  height?: string;
  personality?: string[];
  likes?: string[];
  dislikes?: string[];
  // リンク集
  links?: CharacterLink[];
  // ライセンス設定
  licensing?: {
    enabled: boolean;
    royaltyPercentage: number;
    requiresAttribution: boolean;
    customTerms?: string;
    updatedAt?: string;
  };
}

interface CharacterListResponse {
  characters: CharacterResponse[];
  total: number;
}

// Helper to get auth token
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.getIdToken();
}

// Helper to convert API response to Character type
function apiToCharacter(response: CharacterResponse): Character {
  return {
    id: response.id,
    creatorId: response.creatorId || response.ownerId, // creatorIdがない場合はownerIdをフォールバック
    ownerId: response.ownerId,
    name: response.name,
    nameReading: response.nameReading,
    nameEnglish: response.nameEnglish,
    category: response.category,
    bio: response.bio,
    // XOR-Pix image fields
    noise1Url: response.noise1Url,
    noise2Url: response.noise2Url,
    rid: response.rid,
    imageWidth: response.imageWidth,
    imageHeight: response.imageHeight,
    thumbnailUrl: response.thumbnailUrl,
    // XOR-Pix fallback URLs
    fallbackNoise1Url: response.fallbackNoise1Url,
    fallbackNoise2Url: response.fallbackNoise2Url,
    // Legacy field (backward compatibility)
    avatarUrl: response.avatarUrl || response.thumbnailUrl,
    headerImageUrl: response.headerImageUrl,
    tags: response.tags,
    cardTheme: response.cardTheme,
    themeColors: response.themeColors,
    isPublic: response.isPublic,
    allowFanart: response.allowFanart,
    fanartModerationEnabled: response.fanartModerationEnabled,
    status: response.status,
    stats: response.stats,
    // Creator info
    creatorName: response.creatorName,
    creatorUsername: response.creatorUsername,
    creatorAvatar: response.creatorAvatar,
    // Owner info
    ownerName: response.ownerName,
    ownerUsername: response.ownerUsername,
    ownerAvatar: response.ownerAvatar,
    // AR連携（appeear.me）
    arModelId: response.arModelId,
    arModelStatus: response.arModelStatus,
    // おむかえ（販売）関連
    forSale: response.forSale,
    salePrice: response.salePrice,
    transferCount: response.transferCount,
    // キャラクター設定
    species: response.species,
    gender: response.gender,
    age: response.age,
    birthday: response.birthday,
    height: response.height,
    personality: response.personality,
    likes: response.likes,
    dislikes: response.dislikes,
    // リンク集
    links: response.links,
    // ライセンス設定
    licensing: response.licensing ? {
      enabled: response.licensing.enabled,
      royaltyPercentage: response.licensing.royaltyPercentage,
      requiresAttribution: response.licensing.requiresAttribution,
      customTerms: response.licensing.customTerms,
      updatedAt: response.licensing.updatedAt
        ? { toDate: () => new Date(response.licensing!.updatedAt!) } as any
        : { toDate: () => new Date() } as any,
    } : undefined,
    // Convert ISO strings to Timestamp-like objects for compatibility
    createdAt: { toDate: () => new Date(response.createdAt) } as any,
    updatedAt: { toDate: () => new Date(response.updatedAt) } as any,
  };
}

// ========== Character Operations ==========

export interface CreateCharacterData {
  name: string;
  nameReading?: string;   // 読み仮名
  nameEnglish?: string;   // 英語表記
  category?: 'original' | 'vtuber';  // カテゴリー
  bio?: string;
  catchcopy?: string;  // 特徴・キャッチコピー
  tags?: string[];
  cardTheme?: 'dark' | 'light';
  themeColors?: string[];
  isPublic?: boolean;
  allowFanart?: boolean;
  avatarFile?: File;
  // Character settings
  species?: string;
  gender?: string;
  age?: string;
  personality?: string[];
  likes?: string[];
  dislikes?: string[];
  avatarUrl?: string;
}

/**
 * キャラクター作成（XOR-Pix暗号化対応）
 * 画像をXORシェアに分割してからAPIに送信
 *
 * @param _ownerId - オーナーID（互換性のため保持、未使用）
 * @param data - キャラクターデータ
 * @param onProgress - XOR生成の進捗コールバック (0-100)
 */
export const createCharacter = async (
  _ownerId: string,
  data: CreateCharacterData,
  onProgress?: (progress: number, stage: string) => void
): Promise<string> => {
  if (!data.avatarFile) {
    throw new Error('Avatar file is required');
  }

  const token = await getAuthToken();

  // 1. 画像をXORシェアに分割
  onProgress?.(0, 'encrypting');
  const xorShares: SecureXorShares = await generateSecureXorSharesWithProgress(
    data.avatarFile,
    { maxDimension: 1200, quality: 0.92 },
    (progress, stage) => {
      // XOR生成は全体の0-50%
      onProgress?.(Math.round(progress * 0.5), stage);
    }
  );

  onProgress?.(50, 'uploading');

  // 2. Create FormData with XOR shares
  const formData = new FormData();
  formData.append('name', data.name);
  if (data.nameReading) {
    formData.append('nameReading', data.nameReading);
  }
  if (data.nameEnglish) {
    formData.append('nameEnglish', data.nameEnglish);
  }
  if (data.category) {
    formData.append('category', data.category);
  }
  if (data.bio) {
    formData.append('bio', data.bio);
  }
  if (data.catchcopy) {
    formData.append('catchcopy', data.catchcopy);
  }
  if (data.tags && data.tags.length > 0) {
    formData.append('tags', JSON.stringify(data.tags));
  }
  formData.append('cardTheme', data.cardTheme || 'dark');
  if (data.themeColors && data.themeColors.length > 0) {
    formData.append('themeColors', JSON.stringify(data.themeColors));
  }
  formData.append('isPublic', String(data.isPublic ?? true));
  formData.append('allowFanart', String(data.allowFanart ?? true));

  // XOR-Pix fields
  formData.append('noise1', xorShares.share1, 'noise1.png');
  formData.append('noise2', xorShares.share2, 'noise2.png');
  formData.append('original', xorShares.original, 'original.png');
  formData.append('rid', xorShares.encryptionSeed);
  formData.append('width', String(xorShares.width));
  formData.append('height', String(xorShares.height));

  onProgress?.(60, 'uploading');

  const response = await fetch(`${config.apiBaseUrl}/api/orifoli/characters`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    // APIエラーのdetailを抽出して投げる
    try {
      const errorJson = JSON.parse(errorText);
      if (errorJson.detail) {
        const err = new Error(errorJson.detail);
        (err as any).status = response.status;
        throw err;
      }
    } catch (parseErr) {
      if (parseErr instanceof Error && (parseErr as any).status) throw parseErr;
    }
    throw new Error(`Failed to create character: ${errorText}`);
  }

  onProgress?.(100, 'complete');

  const result: CharacterResponse = await response.json();
  return result.id;
};

export const getCharacter = async (characterId: string): Promise<Character | null> => {
  const token = await getAuthToken();

  const response = await fetch(`${config.apiBaseUrl}/api/orifoli/characters/${characterId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get character: ${errorText}`);
  }

  const result: CharacterResponse = await response.json();
  return apiToCharacter(result);
};

export const updateCharacter = async (
  characterId: string,
  data: Partial<Character>
): Promise<void> => {
  const token = await getAuthToken();

  const response = await fetch(`${config.apiBaseUrl}/api/orifoli/characters/${characterId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update character: ${errorText}`);
  }
};

export const updateCharacterAvatar = async (
  characterId: string,
  avatarFile: File
): Promise<void> => {
  const token = await getAuthToken();

  const formData = new FormData();
  formData.append('avatar', avatarFile);

  const response = await fetch(`${config.apiBaseUrl}/api/orifoli/characters/${characterId}/avatar`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update avatar: ${errorText}`);
  }
};

export const updateCharacterHeaderImage = async (
  characterId: string,
  headerFile: File
): Promise<string> => {
  const token = await getAuthToken();

  const formData = new FormData();
  formData.append('header', headerFile);

  const response = await fetch(`${config.apiBaseUrl}/api/orifoli/characters/${characterId}/header`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update header image: ${errorText}`);
  }

  const result = await response.json();
  return result.headerImageUrl;
};

export const deleteCharacter = async (characterId: string): Promise<void> => {
  const token = await getAuthToken();

  const response = await fetch(`${config.apiBaseUrl}/api/orifoli/characters/${characterId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to delete character: ${errorText}`);
  }
};

export const getUserCharacters = async (_ownerId: string): Promise<Character[]> => {
  const token = await getAuthToken();

  const response = await fetch(`${config.apiBaseUrl}/api/orifoli/characters`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get user characters: ${errorText}`);
  }

  const result: CharacterListResponse = await response.json();
  return result.characters.map(apiToCharacter);
};

export const getPublicCharacters = async (limitCount: number = 20): Promise<Character[]> => {
  const response = await fetch(
    `${config.apiBaseUrl}/api/orifoli/public/characters?limit=${limitCount}`,
    {
      method: 'GET',
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get public characters: ${errorText}`);
  }

  const result: CharacterListResponse = await response.json();
  return result.characters.map(apiToCharacter);
};

// Get a public character by ID (no authentication required)
export const getPublicCharacter = async (characterId: string): Promise<Character | null> => {
  const response = await fetch(
    `${config.apiBaseUrl}/api/orifoli/public/characters/${characterId}`,
    {
      method: 'GET',
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get public character: ${errorText}`);
  }

  const result: CharacterResponse = await response.json();
  return apiToCharacter(result);
};

// ========== Character Image Operations ==========
// Note: These operations still use direct Firestore for now
// TODO: Migrate to API in future iteration

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';

const CHARACTER_IMAGES_COLLECTION = 'oc_character_images';
const FANART_REQUESTS_COLLECTION = 'oc_fanart_requests';
const CHARACTER_FOLLOWS_COLLECTION = 'oc_character_follows';
const CHARACTER_LIKES_COLLECTION = 'oc_character_likes';

export const addCharacterImage = async (
  characterId: string,
  uploaderId: string,
  data: Partial<CharacterImage>
): Promise<string> => {
  const imageRef = doc(collection(db, CHARACTER_IMAGES_COLLECTION));

  const newImage: Partial<CharacterImage> = {
    id: imageRef.id,
    characterId,
    uploaderId,
    imageType: data.imageType || 'official',
    isApproved: data.imageType === 'official',
    order: data.order || 0,
    createdAt: serverTimestamp() as Timestamp,
    ...data
  };

  await setDoc(imageRef, newImage);
  return imageRef.id;
};

export const getCharacterImages = async (
  characterId: string,
  imageType?: 'official' | 'fanart'
): Promise<CharacterImage[]> => {
  let q = query(
    collection(db, CHARACTER_IMAGES_COLLECTION),
    where('characterId', '==', characterId),
    where('isApproved', '==', true),
    orderBy('order', 'asc')
  );

  if (imageType) {
    q = query(
      collection(db, CHARACTER_IMAGES_COLLECTION),
      where('characterId', '==', characterId),
      where('imageType', '==', imageType),
      where('isApproved', '==', true),
      orderBy('order', 'asc')
    );
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => doc.data() as CharacterImage);
};

// ========== Fanart Request Operations ==========

export const submitFanartRequest = async (
  characterId: string,
  artistId: string,
  data: Partial<FanartRequest>
): Promise<string> => {
  const requestRef = doc(collection(db, FANART_REQUESTS_COLLECTION));

  const newRequest: Partial<FanartRequest> = {
    id: requestRef.id,
    characterId,
    artistId,
    status: 'pending',
    createdAt: serverTimestamp() as Timestamp,
    ...data
  };

  await setDoc(requestRef, newRequest);
  return requestRef.id;
};

export const getPendingFanartRequests = async (characterId: string): Promise<FanartRequest[]> => {
  const q = query(
    collection(db, FANART_REQUESTS_COLLECTION),
    where('characterId', '==', characterId),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => doc.data() as FanartRequest);
};

export const approveFanartRequest = async (requestId: string): Promise<void> => {
  const requestRef = doc(db, FANART_REQUESTS_COLLECTION, requestId);
  await updateDoc(requestRef, {
    status: 'approved',
    reviewedAt: serverTimestamp()
  });
};

export const rejectFanartRequest = async (
  requestId: string,
  reason?: string
): Promise<void> => {
  const requestRef = doc(db, FANART_REQUESTS_COLLECTION, requestId);
  await updateDoc(requestRef, {
    status: 'rejected',
    rejectionReason: reason,
    reviewedAt: serverTimestamp()
  });
};

// ========== Follow/Like Operations ==========

export const toggleCharacterFollow = async (
  characterId: string,
  _userId: string,  // Cloud Functionが認証コンテキストからユーザーIDを取得するため未使用
  isCurrentlyFollowing: boolean
): Promise<boolean> => {
  // Cloud Functionを呼び出し（セキュリティルールをバイパス）
  const toggleFollowCharacter = httpsCallable<
    { characterId: string; isFollowing: boolean },
    { success: boolean; isFollowing: boolean; message: string }
  >(functions, 'toggleFollowCharacter');

  const result = await toggleFollowCharacter({
    characterId,
    isFollowing: isCurrentlyFollowing
  });

  return result.data.isFollowing;
};

export const toggleCharacterLike = async (
  characterId: string,
  _userId: string  // Cloud Functionが認証コンテキストからユーザーIDを取得するため未使用
): Promise<{ liked: boolean; likeCount: number }> => {
  // 既存のtoggleLike Cloud Functionをtype: 'character'で呼び出し
  const toggleLikeFn = httpsCallable<
    { modelId: string; type: string },
    { success: boolean; liked: boolean; likeCount: number; message: string }
  >(functions, 'toggleLike');

  const result = await toggleLikeFn({ modelId: characterId, type: 'character' });
  return { liked: result.data.liked, likeCount: result.data.likeCount };
};

export const isFollowingCharacter = async (
  characterId: string,
  userId: string
): Promise<boolean> => {
  const followId = `${userId}_${characterId}`;
  const followRef = doc(db, CHARACTER_FOLLOWS_COLLECTION, followId);
  const followSnap = await getDoc(followRef);
  return followSnap.exists();
};

export const hasLikedCharacter = async (
  characterId: string,
  userId: string
): Promise<boolean> => {
  const likeId = `${userId}_${characterId}`;
  const likeRef = doc(db, CHARACTER_LIKES_COLLECTION, likeId);
  const likeSnap = await getDoc(likeRef);
  return likeSnap.exists();
};

// ========== Followed Characters ==========

export const getFollowedCharacters = async (userId: string): Promise<Character[]> => {
  // 1. Get all character IDs the user follows
  const followsQuery = query(
    collection(db, CHARACTER_FOLLOWS_COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const followsSnapshot = await getDocs(followsQuery);
  const characterIds = followsSnapshot.docs.map(doc => doc.data().characterId);

  if (characterIds.length === 0) {
    return [];
  }

  // 2. Fetch character details from API (public endpoint)
  const characters: Character[] = [];

  // Fetch in batches to avoid too many requests
  const batchSize = 10;
  for (let i = 0; i < characterIds.length; i += batchSize) {
    const batch = characterIds.slice(i, i + batchSize);
    const promises = batch.map(id => getPublicCharacter(id));
    const results = await Promise.all(promises);
    characters.push(...results.filter((c): c is Character => c !== null));
  }

  return characters;
};

// ========== Character Gallery Operations (XOR-Pix) ==========

/** ギャラリー画像の型定義 */
export interface GalleryImage {
  modelId: string;
  title: string;
  description: string;
  creatorId: string;
  characterId: string | null;
  noise1Url: string;
  noise2Url: string;
  rid: string;
  thumbnailUrl: string | null;
  width: number;
  height: number;
  likeCount: number;
  created: string;
  isPublic: boolean;
  isMembersOnly: boolean;
  contentType: string;
  mediaType: 'illustration' | 'photo';
  noReproduction: boolean;
  fallbackNoise1Url?: string;
  fallbackNoise2Url?: string;
}

/**
 * キャラクターのギャラリー画像をアップロード
 *
 * @param characterId - キャラクターID
 * @param file - 画像ファイル
 * @param options - アップロードオプション
 * @param onProgress - 進捗コールバック (0-100, stage)
 * @returns task_id - ステータスポーリング用のタスクID
 */
export const uploadCharacterGalleryImage = async (
  characterId: string,
  file: File,
  options: {
    mediaType?: 'illustration' | 'photo';
    title?: string;
    description?: string;
    showOnFigryneTop?: boolean;  // デフォルトtrue
  } = {},
  onProgress?: (progress: number, stage: string) => void
): Promise<string> => {
  const token = await getAuthToken();

  // 1. 画像をXORシェアに分割
  onProgress?.(0, 'encrypting');
  const xorShares: SecureXorShares = await generateSecureXorSharesWithProgress(
    file,
    { maxDimension: 1200, quality: 0.92 },
    (progress, stage) => {
      // XOR生成は全体の0-50%
      onProgress?.(Math.round(progress * 0.5), stage);
    }
  );

  onProgress?.(50, 'uploading');

  // 2. Create FormData with XOR shares
  const formData = new FormData();
  formData.append('noise1', xorShares.share1, 'noise1.png');
  formData.append('noise2', xorShares.share2, 'noise2.png');
  formData.append('original_image', xorShares.original, 'original.png');
  formData.append('rid', xorShares.encryptionSeed);
  formData.append('width', String(xorShares.width));
  formData.append('height', String(xorShares.height));
  formData.append('content_type', 'original');  // クリエイター自身の作品
  formData.append('media_type', options.mediaType || 'illustration');
  formData.append('no_reproduction', 'true');
  formData.append('is_public', 'true');  // Gallery images are public by default
  formData.append('is_members_only', 'false');
  formData.append('character_id', characterId);  // Link to character
  if (options.title) {
    formData.append('title', options.title);
  }
  if (options.description) {
    formData.append('description', options.description);
  }
  // hideFromTopPage: showOnFigryneTopの逆（デフォルトはtrue=載せる→false）
  const hideFromTop = options.showOnFigryneTop === false;
  formData.append('hide_from_top_page', String(hideFromTop));

  onProgress?.(60, 'uploading');

  const response = await fetch(`${config.apiBaseUrl}/api/xor-pix/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to upload gallery image: ${errorText}`);
  }

  onProgress?.(70, 'processing');

  const result = await response.json();
  return result.task_id;
};

/**
 * ギャラリー画像のアップロードステータスを取得
 *
 * @param taskId - タスクID
 * @returns ステータス情報
 */
export const getGalleryUploadStatus = async (taskId: string): Promise<{
  status: string;
  progress: number;
  detail: string;
  model_id?: string;
  error?: string;
}> => {
  const token = await getAuthToken();

  const response = await fetch(`${config.apiBaseUrl}/api/xor-pix/status/${taskId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get upload status');
  }

  return response.json();
};

/**
 * キャラクターのギャラリー画像一覧を取得
 *
 * @param characterId - キャラクターID
 * @param limit - 取得件数（デフォルト: 20）
 * @param before - ページネーション用: この日時より古い画像を取得
 * @returns ギャラリー画像一覧
 */
export const getCharacterGalleryImages = async (
  characterId: string,
  limit: number = 20,
  before?: string
): Promise<GalleryImage[]> => {
  let url = `${config.apiBaseUrl}/api/xor-pix/feed?character_id=${characterId}&limit=${limit}`;
  if (before) {
    url += `&before=${encodeURIComponent(before)}`;
  }

  const response = await fetch(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get gallery images: ${errorText}`);
  }

  const result = await response.json();
  return result.items as GalleryImage[];
};

/**
 * ギャラリー画像を削除
 *
 * @param modelId - モデルID
 */
export const deleteCharacterGalleryImage = async (modelId: string): Promise<void> => {
  // Ensure user is authenticated (token not used but validates auth)
  await getAuthToken();

  // Delete from Firestore via direct access (models collection)
  const modelRef = doc(db, 'models', modelId);
  const modelSnap = await getDoc(modelRef);

  if (!modelSnap.exists()) {
    throw new Error('Gallery image not found');
  }

  const modelData = modelSnap.data();

  // Verify ownership
  const user = auth.currentUser;
  if (!user || modelData.creatorId !== user.uid) {
    throw new Error('Not authorized to delete this image');
  }

  // Soft delete: set status to deleted
  await updateDoc(modelRef, {
    status: 'deleted',
    deletedAt: serverTimestamp()
  });
};

// =====================
// ファンアート関連
// =====================

/** ファンアート画像の型定義（GalleryImageを拡張） */
export interface FanartImage extends GalleryImage {
  isFanart: boolean;
  fanartStatus: 'pending' | 'approved' | 'rejected';
  isMine: boolean;  // 自分の投稿かどうか
}

/** ファンアートフィードのレスポンス */
export interface FanartFeedResponse {
  items: FanartImage[];
  moderationEnabled: boolean;
  isOwner: boolean;
}

/**
 * キャラクターのファンアート一覧を取得
 *
 * @param characterId - キャラクターID
 * @param limit - 取得件数（デフォルト: 20）
 * @param before - ページネーション用: この日時より古い画像を取得
 * @returns ファンアート一覧とメタデータ
 */
export const getCharacterFanart = async (
  characterId: string,
  limit: number = 20,
  before?: string
): Promise<FanartFeedResponse> => {
  let url = `${config.apiBaseUrl}/api/xor-pix/fanart/${characterId}?limit=${limit}`;
  if (before) {
    url += `&before=${encodeURIComponent(before)}`;
  }

  // 認証トークンを取得（ログイン中の場合）
  let headers: HeadersInit = {};
  try {
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch {
    // 未ログインの場合はトークンなしでリクエスト
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get fanart: ${errorText}`);
  }

  return response.json();
};

/**
 * ファンアートをアップロード
 *
 * @param characterId - キャラクターID
 * @param file - 画像ファイル
 * @param options - アップロードオプション
 * @param onProgress - 進捗コールバック (0-100, stage)
 * @returns task_id - ステータスポーリング用のタスクID
 */
export const uploadFanart = async (
  characterId: string,
  file: File,
  options: {
    showOnFigryneTop?: boolean;  // デフォルトtrue
  } = {},
  onProgress?: (progress: number, stage: string) => void
): Promise<string> => {
  const token = await getAuthToken();

  // 1. 画像をXORシェアに分割
  onProgress?.(0, 'encrypting');
  const xorShares: SecureXorShares = await generateSecureXorSharesWithProgress(
    file,
    { maxDimension: 1200, quality: 0.92 },
    (progress, stage) => {
      onProgress?.(Math.round(progress * 0.5), stage);
    }
  );

  onProgress?.(50, 'uploading');

  // 2. Create FormData with XOR shares
  const formData = new FormData();
  formData.append('noise1', xorShares.share1, 'noise1.png');
  formData.append('noise2', xorShares.share2, 'noise2.png');
  formData.append('original_image', xorShares.original, 'original.png');
  formData.append('rid', xorShares.encryptionSeed);
  formData.append('width', String(xorShares.width));
  formData.append('height', String(xorShares.height));
  formData.append('content_type', 'fanart');
  formData.append('media_type', 'illustration');
  formData.append('no_reproduction', 'true');
  formData.append('is_public', 'true');  // Fanart is public
  formData.append('is_members_only', 'false');
  formData.append('character_id', characterId);
  formData.append('is_fanart', 'true');  // Mark as fanart
  // hideFromTopPage: showOnFigryneTopの逆（デフォルトはtrue=載せる→false）
  const hideFromTop = options.showOnFigryneTop === false;
  formData.append('hide_from_top_page', String(hideFromTop));

  onProgress?.(60, 'uploading');

  const response = await fetch(`${config.apiBaseUrl}/api/xor-pix/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to upload fanart: ${errorText}`);
  }

  onProgress?.(70, 'processing');

  const result = await response.json();
  return result.task_id;
};

/**
 * ファンアートを承認/却下
 *
 * @param modelId - モデルID
 * @param action - 'approve' or 'reject'
 */
export const moderateFanart = async (
  modelId: string,
  action: 'approve' | 'reject'
): Promise<void> => {
  const token = await getAuthToken();

  const formData = new FormData();
  formData.append('action', action);

  const response = await fetch(`${config.apiBaseUrl}/api/xor-pix/fanart/${modelId}/moderate`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to moderate fanart: ${errorText}`);
  }
};
